import { useState, useContext } from 'react';
import { useIntl } from 'react-intl';
// eslint-disable-next-line import/no-unresolved
import { Content } from '@plone/types';
import { getId } from '@plone/volto/helpers/Url/Url';
import { toggleDarkMode } from '@designsystem/stories/01-core/Utilities';
import { LogoProps } from '@designsystem/stories/02-atoms/Logo';
import { ButtonProps } from '@designsystem/stories/02-atoms/Button';
import {
  Header as DSHeader,
  NavigationItemProps,
} from '@designsystem/stories/04-organisms/Header';
import {
  MegaMenu,
  MegaMenuProps,
} from '@designsystem/stories/04-organisms/MegaMenu';
import { MegaMenuContext } from '@designsystem/stories/04-organisms/MegaMenuContext';
import { Sidepanel } from '@designsystem/stories/04-organisms/Sidepanel';
import { SidepanelContext } from '@designsystem/stories/04-organisms/SidepanelContext';
import GlobalSearchPanel from '@fhnw/components/GlobalSearch/GlobalSearchPanel';
import { NavItem } from '@fhnw/types/navItem';
import { buildLanguageItems } from '@fhnw/helpers/DesignSystem/buildLanguages';
import { NormalizableContent } from '@fhnw/helpers/DesignSystem/cardTransformers';
import { normalizeNavigation } from '@fhnw/helpers/DesignSystem/normalizeNavigation';
import SchoolsMenu, { SCHOOLS_MENU_ID } from './SchoolsMenu';
import { messages } from './messages';

interface LanguageItem {
  ['@id']: string;
  language: string;
}

interface UniversityContent extends Content {
  nav_title?: string;
  teaser_title?: string;
}

interface NavContent extends Omit<Content, '@components'> {
  '@components'?: {
    navroot?: { navroot: Content };
    navtree?: NavItem;
    meta?: NavItem;
    footer?: NavItem;
    translations?: { items: LanguageItem[] };
    universities?: UniversityContent[];
    university?: UniversityContent;
  };
}

interface HeaderProps {
  content?: NavContent;
}

const Header = ({ content }: HeaderProps) => {
  const intl = useIntl();
  const [open, doOpen, doClose] = useContext<any>(MegaMenuContext);
  const [sidepanelOpen, doOpenSidepanel, doCloseSidepanel] =
    useContext<any>(SidepanelContext);
  const navtree = normalizeNavigation(content?.['@components']?.navtree);
  const isLandingpage = content?.['@type'] === 'Landingpage';
  const meta = normalizeNavigation(content?.['@components']?.meta);
  const footer = normalizeNavigation(content?.['@components']?.footer);
  const universities: UniversityContent[] | undefined =
    content?.['@components']?.universities;
  const university = content?.['@components']?.university;
  const [currentPage, setCurrentPage] = useState(content?.['@id']);

  if (currentPage !== content?.['@id']) {
    doClose();
    setCurrentPage(content?.['@id']);
  }

  const languages = buildLanguageItems(content);

  const actions: ButtonProps[] = [
    {
      icon: 'moon',
      icon_dark: 'sun',
      title: intl.formatMessage(messages.ToggleDarkMode),
      className: 'header--large-only ga-header-darkmode',
      onClick: toggleDarkMode,
    },
  ];
  const menus: MegaMenuProps[] = [];
  const items: NavigationItemProps[] = [];
  // Always link logo to language root (e.g., /de, /en, /fr)
  const languageRoot = `/${intl.locale}`;
  const logo: LogoProps = {
    title: university?.title || intl.formatMessage(messages.LogoLinkTitle),
    href: languageRoot,
    logo: {
      alt: intl.formatMessage(messages.LogoAlt),
      src: `/logo/fhnw-logo-${intl.locale === 'de' ? 'de' : 'en'}.svg`,
    },
  };

  // Build schools menu (hidden on landingpages, like the navigation)
  if (!isLandingpage && universities && universities.length > 1) {
    menus.push({
      id: SCHOOLS_MENU_ID,
      title: intl.formatMessage(messages.TenSchools),
      subtitle: intl.formatMessage(messages.OneGoal),
      description: intl.formatMessage(messages.SchoolsIntro),
      close: intl.formatMessage(messages.Close),
      children: (
        <SchoolsMenu universities={universities as NormalizableContent[]} />
      ),
    });

    const schoolMenuItem = {
      onClick: () => {
        if (open === SCHOOLS_MENU_ID) {
          doClose();
        } else {
          doOpen(SCHOOLS_MENU_ID);
        }
      },
      'aria-controls': SCHOOLS_MENU_ID,
      'aria-haspopup': 'true',
      'aria-expanded': open === SCHOOLS_MENU_ID,
    };

    items.push({
      label: university
        ? university?.teaser_title || university?.title
        : intl.formatMessage(messages.Schools),
      separate: true,
      icon: 'chevron_down',
      ...schoolMenuItem,
    });

    actions.push({
      icon: 'building_simple',
      title: 'Schools',
      className: 'header--small-only ga-header-schools',
      ...schoolMenuItem,
    });
  }

  const mobileMenu: MegaMenuProps = {
    id: 'menu-mobile',
    logo: logo,
    close: intl.formatMessage(messages.Close),
    distribution: [],
    footerActions: [
      {
        icon: 'moon',
        icon_dark: 'sun',
        title: intl.formatMessage(messages.ToggleDarkMode),
        onClick: toggleDarkMode,
      },
    ],
    footer: [],
    footerMeta: [],
  };

  if (footer.length) {
    mobileMenu.footer = footer.map((group) => ({
      label: group.label,
      items: group.items || [],
    }));
  }

  if (meta.length) {
    mobileMenu.footerMeta = meta;
  }

  // Build menus from navtree (+ no nav entries on landingpages)
  (isLandingpage ? [] : navtree).forEach((item, i) => {
    if (item?.items && item.items.length > 0) {
      const id = `menu-${item.href ? getId(item.href) : i}`;
      menus.push({
        id: id,
        close: intl.formatMessage(messages.Close),
        groups: item.items.map((subitem) => ({
          ...subitem,
          label: subitem.label,
        })),
      });

      mobileMenu.distribution?.push({
        label: item.label,
        groups: item.items,
      });

      item = {
        ...item,
        ...{
          onClick: () => {
            if (open === id) {
              doClose();
            } else {
              doOpen(id);
            }
          },
          'aria-controls': id,
          'aria-haspopup': 'true',
          'aria-expanded': open === id,
        },
      };
    }

    items.push(item);
  });

  // Add search action
  actions.push({
    icon: 'search',
    title: intl.formatMessage(messages.StartSearch),
    onClick: (
      e: MouseEvent | React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      e.preventDefault();
      e.stopPropagation();
      doOpenSidepanel('sidepanel-search');
      const actualSearchInput = document.getElementById('searchbar-input');
      if (actualSearchInput) {
        actualSearchInput.focus();
      }
    },
    className: 'ga-header-search',
    'aria-controls': 'sidepanel-search',
    'aria-haspopup': 'true',
    'aria-expanded': sidepanelOpen === 'sidepanel-search',
  });

  actions.push({
    icon: 'menu',
    title: intl.formatMessage(messages.OpenNavigation),
    className: 'header--small-only ga-header-mobile-menu',
    onClick: () => {
      if (open === 'menu-mobile') {
        doClose();
      } else {
        doOpen('menu-mobile');
      }
    },
    'aria-controls': 'menu-mobile',
    'aria-haspopup': 'true',
    'aria-expanded': open === 'menu-mobile',
  });

  menus.push(mobileMenu);

  return (
    <>
      <DSHeader
        logo={logo}
        items={items}
        actions={actions}
        languages={languages}
      />
      {menus.map((menu) => (
        <MegaMenu key={menu.id} {...menu} />
      ))}
      <Sidepanel
        id="sidepanel-search"
        close={intl.formatMessage(messages.Close)}
      >
        <GlobalSearchPanel closeGlobalSearch={doCloseSidepanel} />
      </Sidepanel>
    </>
  );
};

export default Header;
